import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Search, X, ChevronDown, Calendar as CalendarIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverAnchor, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";

/**
 * Gmail-native style search experience for the inbox.
 *
 * Layout:
 *   ┌──────────────────────────────────────────────┐
 *   │ 🔍 Search emails…                       ▾ ⌥ │  ← anchored input
 *   └──────────────────────────────────────────────┘
 *   ↓ (opens below)
 *   ┌──────────────────────────────────────────────┐
 *   │ From         [_______________________]       │
 *   │ To           [_______________________]       │
 *   │ Subject      [_______________________]       │
 *   │ Includes…    [_______________________]       │
 *   │ Doesn't have [_______________________]       │
 *   │ Date within  [1 week ▾]  [pick date 📅]      │
 *   │ Search       [All Mail ▾]                    │
 *   │ ☐ Has attachment                             │
 *   │                                              │
 *   │                          [Clear]  [Search]   │
 *   └──────────────────────────────────────────────┘
 *
 * On Search, we compile the form into Gmail operator syntax (the same
 * grammar `/api/inbox?gmail_q=…` already understands) and call
 * `onSubmit(query)`. The parent (Triage) sets `search` to that string
 * and the existing inbox loader pipes it through to Gmail.
 *
 * When `value` is non-empty AND no advanced form is in play, we also
 * surface a row of quick-filter chips above the inbox list (rendered
 * separately by `InboxSearchChips` below).
 */

const DATE_WINDOWS = [
  { value: "any", label: "Any time" },
  { value: "1d", label: "1 day" },
  { value: "3d", label: "3 days" },
  { value: "1w", label: "1 week" },
  { value: "2w", label: "2 weeks" },
  { value: "1m", label: "1 month" },
  { value: "2m", label: "2 months" },
  { value: "6m", label: "6 months" },
  { value: "1y", label: "1 year" },
];

const MAILBOX_SCOPES = [
  { value: "all", label: "All Mail" },
  { value: "inbox", label: "Inbox" },
  { value: "starred", label: "Starred" },
  { value: "sent", label: "Sent Mail" },
  { value: "drafts", label: "Drafts" },
  { value: "chats", label: "Chats" },
  { value: "trash", label: "Trash" },
  { value: "spam", label: "Spam" },
];

// Build a Gmail operator query string from the advanced form state.
// We quote multi-word free-text fields so `subject:my invoice` doesn't
// only match `my` and leak `invoice` into the body match.
function compileQuery(form) {
  const parts = [];
  const wrap = (v) => {
    const s = (v || "").trim();
    if (!s) return "";
    return /\s/.test(s) ? `"${s}"` : s;
  };
  if (form.from) parts.push(`from:${wrap(form.from)}`);
  if (form.to) parts.push(`to:${wrap(form.to)}`);
  if (form.subject) parts.push(`subject:${wrap(form.subject)}`);
  if (form.includes) {
    // Free text — keep as-is so users can chain their own operators
    // inside (e.g. "invoice OR receipt").
    parts.push(form.includes.trim());
  }
  if (form.excludes) {
    // -"phrase" if multi-word, else -word.
    const s = form.excludes.trim();
    parts.push(/\s/.test(s) ? `-"${s}"` : `-${s}`);
  }
  if (form.hasAttachment) parts.push("has:attachment");
  if (form.scope && form.scope !== "all") parts.push(`in:${form.scope}`);
  if (form.dateWindow && form.dateWindow !== "any") {
    // Gmail's `newer_than:` accepts d/w/m/y suffixes natively.
    // We translate our window codes into that exact grammar.
    const map = { "1d": "1d", "3d": "3d", "1w": "7d", "2w": "14d", "1m": "1m", "2m": "2m", "6m": "6m", "1y": "1y" };
    const v = map[form.dateWindow];
    if (v) parts.push(`newer_than:${v}`);
  }
  if (form.dateExact) {
    // YYYY/MM/DD — Gmail's date format. We anchor as `before:` so the
    // "within X of [date]" pair narrows the window cleanly.
    try {
      parts.push(`before:${format(form.dateExact, "yyyy/MM/dd")}`);
    } catch {
      /* ignore invalid date */
    }
  }
  return parts.join(" ").trim();
}

const EMPTY_FORM = {
  from: "",
  to: "",
  subject: "",
  includes: "",
  excludes: "",
  hasAttachment: false,
  scope: "all",
  dateWindow: "any",
  dateExact: null,
};

export default function InboxSearchDropdown({ value, onChange, onSubmit, onClear, inputClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const inputRef = useRef(null);

  // Listen for the global Cmd/Ctrl-K shortcut. The dispatcher (AppShell)
  // fires a custom event so any route can trigger us without prop drilling.
  useEffect(() => {
    const handler = () => {
      setOpen(true);
      setTimeout(() => inputRef.current?.focus(), 50);
    };
    window.addEventListener("inbox:focus-search", handler);
    return () => window.removeEventListener("inbox:focus-search", handler);
  }, []);

  const submit = useCallback(() => {
    const q = compileQuery(form);
    onSubmit?.(q);
    setOpen(false);
  }, [form, onSubmit]);

  const reset = useCallback(() => {
    setForm(EMPTY_FORM);
    onChange?.("");
    onClear?.();
  }, [onChange, onClear]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={`relative ${inputClassName}`}>
        <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
        <PopoverAnchor asChild>
          <Input
            ref={inputRef}
            data-testid="inbox-search"
            placeholder="Search emails…"
            value={value || ""}
            onChange={(e) => onChange?.(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSubmit?.(value || "");
                setOpen(false);
              } else if (e.key === "Escape") {
                setOpen(false);
              }
            }}
            onFocus={() => setOpen(true)}
            className="pl-10 pr-20 h-10 bg-slate-100 border-transparent hover:bg-slate-50 focus-visible:bg-white focus-visible:border-slate-200 rounded-lg"
          />
        </PopoverAnchor>
        {value ? (
          <button
            type="button"
            data-testid="inbox-search-clear"
            onClick={(e) => {
              e.stopPropagation();
              reset();
            }}
            className="absolute right-10 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-slate-200/70 text-slate-500"
            title="Clear search"
          >
            <X className="h-3.5 w-3.5"/>
          </button>
        ) : null}
        <button
          type="button"
          data-testid="inbox-search-advanced-toggle"
          onClick={(e) => {
            e.stopPropagation();
            setOpen((v) => !v);
          }}
          className={`absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded transition-colors ${open ? "bg-slate-200 text-slate-700" : "hover:bg-slate-200/70 text-slate-500"}`}
          title="Show search options"
        >
          <ChevronDown className="h-3.5 w-3.5"/>
        </button>
      </div>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-[640px] p-0 rounded-xl shadow-2xl border-slate-200"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          // Don't close the popover when the user clicks back into the
          // search input (which is the anchor, not the trigger). Without
          // this Radix treats the input as "outside" and slams the panel
          // shut as soon as the user clicks in to type more.
          const t = e.target;
          if (t && t.closest && t.closest('[data-testid="inbox-search"]')) {
            e.preventDefault();
          }
        }}
        data-testid="inbox-search-dropdown"
      >
        <div className="px-5 py-4 space-y-3">
          <SearchRow label="From">
            <Input
              data-testid="search-from"
              value={form.from}
              onChange={(e) => setForm({ ...form, from: e.target.value })}
              placeholder=""
              className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0"
            />
          </SearchRow>
          <SearchRow label="To">
            <Input
              data-testid="search-to"
              value={form.to}
              onChange={(e) => setForm({ ...form, to: e.target.value })}
              className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0"
            />
          </SearchRow>
          <SearchRow label="Subject">
            <Input
              data-testid="search-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0"
            />
          </SearchRow>
          <SearchRow label="Includes the words">
            <Input
              data-testid="search-includes"
              value={form.includes}
              onChange={(e) => setForm({ ...form, includes: e.target.value })}
              className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0"
            />
          </SearchRow>
          <SearchRow label="Doesn't have">
            <Input
              data-testid="search-excludes"
              value={form.excludes}
              onChange={(e) => setForm({ ...form, excludes: e.target.value })}
              className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0"
            />
          </SearchRow>
          <SearchRow label="Date within">
            <div className="flex gap-3 items-center">
              <Select
                value={form.dateWindow}
                onValueChange={(v) => setForm({ ...form, dateWindow: v })}
              >
                <SelectTrigger
                  data-testid="search-date-window"
                  className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0 w-40"
                >
                  <SelectValue placeholder="Any time" />
                </SelectTrigger>
                <SelectContent>
                  {DATE_WINDOWS.map((d) => (
                    <SelectItem key={d.value || "any"} value={d.value}>{d.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="search-date-exact"
                    className="h-9 px-2 text-sm text-slate-600 border-b border-slate-200 flex items-center gap-2 hover:text-slate-900 transition-colors"
                  >
                    {form.dateExact ? format(form.dateExact, "dd MMM yyyy") : "of date"}
                    <CalendarIcon className="h-4 w-4 text-slate-400"/>
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-auto" align="start">
                  <Calendar
                    mode="single"
                    selected={form.dateExact}
                    onSelect={(d) => setForm({ ...form, dateExact: d })}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </SearchRow>
          <SearchRow label="Search">
            <Select
              value={form.scope}
              onValueChange={(v) => setForm({ ...form, scope: v })}
            >
              <SelectTrigger
                data-testid="search-scope"
                className="h-9 border-0 border-b border-slate-200 rounded-none px-0 focus-visible:border-blue-600 focus-visible:ring-0"
              >
                <SelectValue placeholder="All Mail" />
              </SelectTrigger>
              <SelectContent>
                {MAILBOX_SCOPES.map((s) => (
                  <SelectItem key={s.value || "all"} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SearchRow>
          <div className="pt-1 flex items-center gap-2">
            <Checkbox
              id="search-has-attachment"
              data-testid="search-has-attachment"
              checked={form.hasAttachment}
              onCheckedChange={(c) => setForm({ ...form, hasAttachment: !!c })}
            />
            <label htmlFor="search-has-attachment" className="text-sm text-slate-700 cursor-pointer select-none">
              Has attachment
            </label>
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end items-center gap-3 bg-slate-50/60 rounded-b-xl">
          <button
            type="button"
            data-testid="search-reset"
            onClick={reset}
            className="text-[13px] text-slate-600 hover:text-slate-900 px-3 py-1.5 transition-colors"
          >
            Clear
          </button>
          <Button
            data-testid="search-submit"
            onClick={submit}
            className="bg-blue-600 hover:bg-blue-700 text-white h-9 px-6 rounded-full text-[13px] font-medium"
          >
            Search
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function SearchRow({ label, children }) {
  return (
    <div className="grid grid-cols-[120px_1fr] items-center gap-4">
      <div className="text-[13px] text-slate-600 leading-tight">{label}</div>
      <div>{children}</div>
    </div>
  );
}

/**
 * Quick-filter chip strip shown above the inbox list when a search is active.
 * Mirrors Gmail's post-search toolbar: From ▾ · Any time ▾ · Has attachment ·
 * To ▾ · Is unread · ✕ Clear.
 *
 * Each chip toggles a Gmail-operator clause inside the active query string.
 * Toggling a chip rewrites the parent `value` so the inbox refetches.
 */
export function InboxSearchChips({ value, onChange, onClear }) {
  // Don't render until there is an active search.
  const hasQuery = !!(value || "").trim();
  if (!hasQuery) return null;

  const has = (re) => re.test(value);
  const toggle = (matcher, addition) => {
    if (has(matcher)) {
      onChange?.(value.replace(matcher, "").replace(/\s+/g, " ").trim());
    } else {
      onChange?.(`${value} ${addition}`.replace(/\s+/g, " ").trim());
    }
  };

  const hasAttachmentRe = /\bhas:attachment\b/i;
  const isUnreadRe = /\bis:unread\b/i;

  return (
    <div className="px-4 py-2 border-b border-slate-200 bg-white flex items-center gap-2 flex-wrap" data-testid="inbox-search-chips">
      <Chip
        active={hasAttachmentRe.test(value)}
        onClick={() => toggle(hasAttachmentRe, "has:attachment")}
        testid="chip-has-attachment"
      >
        Has attachment
      </Chip>
      <Chip
        active={isUnreadRe.test(value)}
        onClick={() => toggle(isUnreadRe, "is:unread")}
        testid="chip-is-unread"
      >
        Is unread
      </Chip>
      <div className="ml-auto flex items-center gap-2">
        <span className="text-[11px] text-slate-400 truncate max-w-[280px]" title={value}>
          {value}
        </span>
        <button
          type="button"
          onClick={onClear}
          data-testid="chip-clear-all"
          className="p-1 rounded hover:bg-slate-100 text-slate-500"
          title="Clear search"
        >
          <X className="h-3.5 w-3.5"/>
        </button>
      </div>
    </div>
  );
}

function Chip({ children, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`text-[12px] h-7 px-3 rounded-full border transition-colors ${
        active
          ? "bg-blue-50 border-blue-300 text-blue-700"
          : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}
